/**
 * "Shoppers can't create new art": the merchant-facing words for each reason the server's storeArtAvailability
 * (src/server/domain/billing.ts) gives, shared by the app-wide banner and the blocked/recovered e-mails, so both say
 * the same thing. Short, in English; the way out is one action.
 */

export type ArtBlockReasonCode =
  | 'no_plan'
  | 'plan_inactive'
  | 'billing_frozen'
  | 'merchant_daily_limit'
  | 'trial_daily_limit'
  | 'trial_credits_exhausted'
  | 'included_exhausted'
  | 'cap_reached';

/** What the action does: open Plan & usage (extras, cap), Shopify's plan page, Shopify's billing settings, or Support. */
export type ArtBlockAction = 'enable_extras' | 'raise_cap' | 'choose_plan' | 'shopify_billing' | 'support' | 'wait';

export interface ArtBlockCopy {
  /** Completes "Shoppers can't create new art: …". */
  reason: string;
  action: ArtBlockAction;
  /** The button's label (none for 'wait'). */
  label: string | null;
}

export const ART_BLOCK_COPY: Record<ArtBlockReasonCode, ArtBlockCopy> = {
  no_plan: { reason: 'your store has no plan yet', action: 'choose_plan', label: 'Choose a plan' },
  plan_inactive: { reason: 'your Perzo plan is no longer active', action: 'choose_plan', label: 'Choose or re-approve plan' },
  billing_frozen: { reason: 'Shopify froze your Perzo subscription', action: 'shopify_billing', label: 'Review Shopify billing' },
  merchant_daily_limit: { reason: 'your store reached its daily generation limit', action: 'support', label: 'Ask to raise it' },
  trial_daily_limit: { reason: 'the free trial’s daily limit is reached', action: 'wait', label: null },
  trial_credits_exhausted: { reason: 'the free trial’s credits are used up', action: 'wait', label: null },
  included_exhausted: { reason: 'the included credits for this billing cycle are used up', action: 'enable_extras', label: 'Enable extra generations' },
  cap_reached: { reason: 'your overage cap for this billing cycle is reached', action: 'raise_cap', label: 'Raise cap' },
};

export const ART_BLOCK_HEADLINE = 'Shoppers can’t create new art';

/** "Resumes …" for a known time: within a day as a time, else as a date (UTC, as the server decides). */
export function resumeText(reason: ArtBlockReasonCode, at: Date | null, now: Date): string | null {
  if (!at) return null;
  const sameDay = at.getTime() - now.getTime() < 86_400_000;
  const when = sameDay
    ? `${at.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'UTC' })} UTC`
    : at.toLocaleDateString('en-US', { month: 'long', day: 'numeric', timeZone: 'UTC' });
  if (reason === 'trial_credits_exhausted') return `Resumes when your paid billing cycle starts (${when}).`;
  if (reason === 'included_exhausted' || reason === 'cap_reached') return `Otherwise it resumes ${sameDay ? 'at' : 'on'} ${when}, with the next billing cycle.`;
  return `Resumes ${sameDay ? 'at' : 'on'} ${when}.`;
}
